import { useId } from "react";

export default function GenderTransManIconMini({
  size = 28,
  active = false,
  title = "Trans Man",
  ...rest
}) {
  const uid = useId();
  const id = (name) => `${uid}-${name}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      style={{ display: "block" }}
      data-preserve
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Outer glow */}
        <radialGradient id={id("rimGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#00101f" stopOpacity="0" />
          <stop offset="85%" stopColor="#5AA8FF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FF6DA8" stopOpacity="0.75" />
        </radialGradient>

        {/* Rim stroke gradient (trans flag vibe) */}
        <linearGradient
          id={id("rimStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#5AA8FF" />
          <stop offset="40%" stopColor="#ffffff" />
          <stop offset="80%" stopColor="#FF6DA8" />
          <stop offset="100%" stopColor="#5AA8FF" />
        </linearGradient>

        {/* Inner face fill */}
        <radialGradient id={id("faceFill")} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#12172a" />
          <stop offset="70%" stopColor="#050811" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Soft highlight on the upper rim */}
        <radialGradient id={id("rimHighlight")} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Symbol stroke gradient */}
        <linearGradient
          id={id("symbolStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#5AA8FF" />
          <stop offset="35%" stopColor="#ffffff" />
          <stop offset="70%" stopColor="#FF6DA8" />
          <stop offset="100%" stopColor="#5AA8FF" />
        </linearGradient>

        {/* Glow for the symbol */}
        <filter
          id={id("symbolGlow")}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.1" result="b1" />
          <feColorMatrix
            in="b1"
            type="matrix"
            values="
              0 0 0 0 0.6
              0 0 0 0 0.8
              0 0 0 0 1
              0 0 0 1 0
            "
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Active aura around the whole badge */}
        <filter
          id={id("activeAura")}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="6" result="b1" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Tiny spark glows */}
        <filter id={id("spark")} x="-200%" y="-200%" width="400%" height="400%">
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>

      {/* ===== Badge base ===== */}
      <g filter={active ? `url(#${id("activeAura")})` : undefined}>
        <circle cx="32" cy="32" r="30" fill={`url(#${id("rimGlow")})`} />
        <circle
          cx="32"
          cy="32"
          r="30"
          fill="none"
          stroke={`url(#${id("rimStroke")})`}
          strokeWidth="4"
        />
        <circle
          cx="32"
          cy="24"
          r="26"
          fill={`url(#${id("rimHighlight")})`}
          opacity="0.4"
        />
        <circle cx="32" cy="32" r="24" fill={`url(#${id("faceFill")})`} />
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.16"
          strokeWidth="1.25"
        />
      </g>

      {/* ===== Trans Man symbol (modified Mars with extra bar) ===== */}
      <g
        filter={`url(#${id("symbolGlow")})`}
        stroke={`url(#${id("symbolStroke")})`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Circle base */}
        <circle cx="27" cy="35" r="7" />

        {/* Diagonal arrow from circle to top-right */}
        <line x1="31.5" y1="30.5" x2="41" y2="21" />

        {/* Arrow head */}
        <line x1="37" y1="21" x2="41" y2="21" />
        <line x1="41" y1="17" x2="41" y2="21" />

        {/* Small extra bar along the arrow shaft (hinting at "transition" / layering) */}
        <line x1="34" y1="28" x2="37" y2="25" strokeWidth="2.4" />
      </g>

      {/* ===== Little twinkling sparks ===== */}
      <g opacity="0.9">
        <circle
          cx="20"
          cy="22"
          r="0.8"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="44"
          cy="18"
          r="0.6"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="48"
          cy="36"
          r="0.7"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="18"
          cy="40"
          r="0.5"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
      </g>
    </svg>
  );
}
